import { useQuery, useQueryClient } from "@tanstack/react-query";

import { fetchCall } from "../api/calls";
import { ApiError } from "../lib/api";
import { useMqttSubscription } from "./useMqtt";

// A freshly-dispatched call has no DynamoDB row until the worker reaches
// call-start (worker writes it at session.start, AFTER the SIP dial begins).
// Until then GET /api/calls/{id} returns 404 "call_not_found". When an
// operator jumps straight to the live-call view right after dispatch, that
// 404 is expected and transient — not an error. We keep retrying for this
// many attempts (× CONNECTING_RETRY_DELAY_MS ≈ 60s) so the page resolves the
// moment the call connects, instead of surfacing a hard error.
const CONNECTING_MAX_RETRIES = 30;
const CONNECTING_RETRY_DELAY_MS = 2_000;

function isCallNotStartedYet(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export function useCall(id: string | undefined) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["call", id],
    queryFn: () => fetchCall(id!),
    enabled: !!id,
    // 404 = call row not written yet (still dialing/connecting). Keep
    // retrying at a steady cadence so the view flips to live as soon as the
    // worker persists call_started. Other errors get the usual short backoff.
    retry: (failureCount, error) =>
      isCallNotStartedYet(error) ? failureCount < CONNECTING_MAX_RETRIES : failureCount < 3,
    retryDelay: (attempt, error) =>
      isCallNotStartedYet(error)
        ? CONNECTING_RETRY_DELAY_MS
        : Math.min(1_000 * 2 ** attempt, 30_000),
    // Backstop when MQTT is idle or disconnected. Active only while LIVE
    // so ended calls don't keep firing requests.
    refetchInterval: (query) => {
      const data = query.state.data as { record?: { state?: string } } | undefined;
      return data?.record?.state === "LIVE" ? 5_000 : false;
    },
  });

  useMqttSubscription(id ? `call/${id}/turn` : "noop", () => {
    if (id) void qc.invalidateQueries({ queryKey: ["call", id] });
  });
  useMqttSubscription(id ? `call/${id}/state` : "noop", () => {
    if (id) void qc.invalidateQueries({ queryKey: ["call", id] });
  });
  useMqttSubscription(id ? `call/${id}/audit` : "noop", () => {
    if (id) void qc.invalidateQueries({ queryKey: ["call", id] });
  });

  return q;
}
